import { useEffect, useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  IconButton,
  Typography,
  Box,
  Paper,
  CircularProgress,
  Divider,
  Stack,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import Select from "react-select";
import { getSingleSelectStyles, getMultiSelectStyles } from "@/lib/styles";
import { useTheme, alpha } from "@mui/material/styles";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputLabel from "@mui/material/InputLabel";
import InputAdornment from "@mui/material/InputAdornment";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import PersonIcon from "@mui/icons-material/Person";
import LockIcon from "@mui/icons-material/Lock";
import BusinessIcon from "@mui/icons-material/Business";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import EmailIcon from "@mui/icons-material/Email";
import SecurityIcon from "@mui/icons-material/Security";
import makeAnimated from "react-select/animated";
import usePostJsonData from "@/hooks/postHooks/usePostJsonData";
import useGetMetadata from "@/hooks/useGetMetaData";
import PropTypes from "prop-types";
import CloseIcon from "@mui/icons-material/Close";
import Toast from "@/components/toast";
import useApiErrorHandler from "@/hooks/useApiErrorHandler";
import { useTranslation } from "react-i18next";

const animatedComponents = makeAnimated();

const AddUser = ({ onClose, onUserAdd }) => {
  const { t } = useTranslation();
  const { isLoading, SitesList, UsersList, Departments, userGroupsList } =
    useGetMetadata();
  const { handleApiError, toast, setToast, handleToastClose } =
    useApiErrorHandler();
  const { mutate: postUser } = usePostJsonData();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const theme = useTheme();
  const singleSelectStyles = getSingleSelectStyles(theme);
  const multiSelectStyles = getMultiSelectStyles(theme);

  const DEFAULTDATA = {
    first_name: "",
    last_name: "",
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    user_type: null,
    manager_id: null,
    department_id: null,
    user_groups: [],
    site_ids: [],
    work_permit_visibility: null,
    observation_visibility: null,
  };

  const [first_nameFocused, setFirst_nameFocused] = useState(false);
  const [last_nameFocused, setLast_nameFocused] = useState(false);
  const [usernameFocused, setUsernameFocused] = useState(false);
  const [emailFocused, setEmailFocused] = useState(false);
  const [user_typeFocused, setUser_typeFocused] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [confirmPasswordFocused, setConfirmPasswordFocused] = useState(false);
  const [userGroupsFocused, setUserGroupsFocused] = useState(false);
  const [visibilitySettingsFocused, setVisibilitySettingsFocused] =
    useState(false);

  const [userData, setUserData] = useState(DEFAULTDATA);
  const [formValid, setFormValid] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (event) => event.preventDefault();
  const handleMouseUpPassword = (event) => event.preventDefault();

  const sentData = {
    username: userData.username,
    email: userData.email,
    first_name: userData.first_name,
    last_name: userData.last_name,
    password: userData.password,
    user_type: userData.user_type?.value,
    user_groups: userData.user_groups.map((group) => group.value),
    site_ids: userData.site_ids.map((site) => site.value),
    manager_id: userData.manager_id?.value,
    department_id: userData.department_id?.value,
    document_visibility_settings: [
      userData.work_permit_visibility && {
        document_type: "WORK_PERMIT",
        visibility_type: userData.work_permit_visibility.value,
      },
      userData.observation_visibility && {
        document_type: "OBSERVATION",
        visibility_type: userData.observation_visibility.value,
      },
    ].filter(Boolean),
  };

  const handleMultiSelect = (name) => (e) =>
    setUserData((prev) => ({
      ...prev,
      [name]: e.map((x) => ({ value: x.value, label: x.label })),
    }));

  const handleTextChange = (field) => (e) =>
    setUserData((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSingleSelect = (name) => (e) =>
    setUserData((prev) => ({
      ...prev,
      [name]: e ? { value: e.value, label: e.label } : null,
    }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formValid) return;
    setIsSubmitting(true);

    postUser(
      { url: "/api/v1/org-admin/users", data: sentData },
      {
        onSuccess: async () => {
          await onUserAdd();
          setUserData(DEFAULTDATA);
          setToast({
            open: true,
            message: t("addUser.successToast"),
            severity: "success",
          });
          setIsSubmitting(false);
          onClose();
        },
        onError: (err) => {
          handleApiError(err.response.data);
          setIsSubmitting(false);
        },
      }
    );
  };

  const userType = [
    { value: "ORG_ADMIN", label: t("addUser.userTypeOrgAdmin") },
    { value: "ORG_USER", label: t("addUser.userTypeOrgUser") },
  ];

  const visibilityTypes = [
    { value: "ALL", label: t("addUser.visibilityAll") },
    { value: "SITE_BASED", label: t("addUser.visibilitySiteBased") },
    {
      value: "INVOLVEMENT_BASED",
      label: t("addUser.visibilityInvolvementBased"),
    },
  ];

  const checkFormValidity = useCallback(() => {
    const isValid =
      userData.first_name &&
      userData.last_name &&
      userData.username &&
      userData.password &&
      userData.confirmPassword === userData.password &&
      userData.user_type &&
      userData.user_groups.length > 0 &&
      userData.work_permit_visibility &&
      userData.observation_visibility;
    setFormValid(isValid);
  }, [userData]);

  useEffect(() => {
    checkFormValidity();
  }, [userData, checkFormValidity]);

  if (isLoading) return <Typography>{t("addUser.loadingText")}</Typography>;

  return (
    <>
      <Dialog
        open
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: 3,
            maxHeight: "90vh",
            boxShadow: theme.shadows[24],
          },
        }}
        aria-labelledby="add-user-dialog-title"
      >
        <DialogTitle
          id="add-user-dialog-title"
          component="div"
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: theme.palette.primary.contrastText,
            position: "relative",
            py: 3,
            px: 4,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <PersonAddIcon sx={{ fontSize: 32 }} />
            <Typography
              variant="h4"
              component="h2"
              sx={{
                fontWeight: 700,
                letterSpacing: "-0.02em",
              }}
            >
              {t("addUser.title")}
            </Typography>
          </Box>
          <Typography
            variant="subtitle1"
            sx={{
              opacity: 0.9,
              fontWeight: 400,
              mt: 1,
            }}
          >
            {t("addUser.subtitle")}
          </Typography>
          <IconButton
            onClick={onClose}
            sx={{
              position: "absolute",
              right: 16,
              top: 16,
              color: theme.palette.primary.contrastText,
              "&:hover": {
                bgcolor: alpha(theme.palette.primary.contrastText, 0.1),
              },
            }}
            aria-label={t("addUser.closeDialogAria")}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0, overflowY: "auto" }}>
          <Box sx={{ p: 4 }}>
            <form onSubmit={handleSubmit} aria-label={t("addUser.formAria")}>
              <Stack spacing={4}>
                {/* Personal Information Section */}
                <Paper
                  elevation={2}
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 48,
                        height: 48,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        mr: 2,
                      }}
                    >
                      <PersonIcon sx={{ color: theme.palette.primary.main }} />
                    </Box>
                    <Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: theme.palette.text.primary,
                        }}
                      >
                        {t("addUser.personalInfoTitle")}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t("addUser.personalInfoDescription")}
                      </Typography>
                    </Box>
                  </Box>

                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        label={t("addUser.firstNameLabel")}
                        fullWidth
                        value={userData.first_name}
                        onChange={handleTextChange("first_name")}
                        onBlur={() => setFirst_nameFocused(true)}
                        error={first_nameFocused && !userData.first_name}
                        helperText={
                          first_nameFocused && !userData.first_name
                            ? t("addUser.firstNameError")
                            : ""
                        }
                        aria-label={t("addUser.firstNameAria")}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                          },
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        label={t("addUser.lastNameLabel")}
                        fullWidth
                        value={userData.last_name}
                        onChange={handleTextChange("last_name")}
                        onBlur={() => setLast_nameFocused(true)}
                        error={last_nameFocused && !userData.last_name}
                        helperText={
                          last_nameFocused && !userData.last_name
                            ? t("addUser.lastNameError")
                            : ""
                        }
                        aria-label={t("addUser.lastNameAria")}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                          },
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        label={t("addUser.usernameLabel")}
                        fullWidth
                        value={userData.username}
                        onChange={handleTextChange("username")}
                        onBlur={() => setUsernameFocused(true)}
                        error={usernameFocused && !userData.username}
                        helperText={
                          usernameFocused && !userData.username
                            ? t("addUser.usernameError")
                            : ""
                        }
                        aria-label={t("addUser.usernameAria")}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                          },
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        label={t("addUser.emailLabel")}
                        fullWidth
                        type="email"
                        value={userData.email}
                        onChange={handleTextChange("email")}
                        onBlur={() => setEmailFocused(true)}
                        error={emailFocused && !userData.email}
                        helperText={
                          emailFocused && !userData.email
                            ? t("addUser.emailError")
                            : ""
                        }
                        aria-label={t("addUser.emailAria")}
                        slotProps={{
                          input: {
                            startAdornment: (
                              <InputAdornment position="start">
                                <EmailIcon color="action" />
                              </InputAdornment>
                            ),
                          },
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </Paper>

                {/* Account Security Section */}
                <Paper
                  elevation={2}
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 48,
                        height: 48,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.warning.main, 0.1),
                        mr: 2,
                      }}
                    >
                      <LockIcon sx={{ color: theme.palette.warning.main }} />
                    </Box>
                    <Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: theme.palette.text.primary,
                        }}
                      >
                        {t("addUser.accountDetailsTitle")}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t("addUser.accountDetailsDescription")}
                      </Typography>
                    </Box>
                  </Box>

                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <FormControl
                        fullWidth
                        variant="outlined"
                        error={passwordFocused && !userData.password}
                      >
                        <InputLabel>{t("addUser.passwordLabel")}</InputLabel>
                        <OutlinedInput
                          label={t("addUser.passwordLabel")}
                          value={userData.password}
                          onChange={handleTextChange("password")}
                          onBlur={() => setPasswordFocused(true)}
                          type={showPassword ? "text" : "password"}
                          startAdornment={
                            <InputAdornment position="start">
                              <LockIcon color="action" />
                            </InputAdornment>
                          }
                          endAdornment={
                            <InputAdornment position="end">
                              <IconButton
                                onClick={handleClickShowPassword}
                                onMouseDown={handleMouseDownPassword}
                                onMouseUp={handleMouseUpPassword}
                                edge="end"
                                aria-label={t(
                                  "addUser.togglePasswordVisibilityAria"
                                )}
                              >
                                {showPassword ? (
                                  <Visibility />
                                ) : (
                                  <VisibilityOff />
                                )}
                              </IconButton>
                            </InputAdornment>
                          }
                          sx={{ borderRadius: 2 }}
                        />
                        {passwordFocused && !userData.password && (
                          <Typography
                            color="error"
                            variant="caption"
                            sx={{ mt: 1 }}
                          >
                            {t("addUser.passwordError")}
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <FormControl
                        fullWidth
                        variant="outlined"
                        error={
                          confirmPasswordFocused &&
                          userData.confirmPassword !== userData.password
                        }
                      >
                        <InputLabel>
                          {t("addUser.confirmPasswordLabel")}
                        </InputLabel>
                        <OutlinedInput
                          label={t("addUser.confirmPasswordLabel")}
                          value={userData.confirmPassword}
                          onChange={handleTextChange("confirmPassword")}
                          onBlur={() => setConfirmPasswordFocused(true)}
                          type={showPassword ? "text" : "password"}
                          startAdornment={
                            <InputAdornment position="start">
                              <LockIcon color="action" />
                            </InputAdornment>
                          }
                          endAdornment={
                            <InputAdornment position="end">
                              <IconButton
                                onClick={handleClickShowPassword}
                                onMouseDown={handleMouseDownPassword}
                                onMouseUp={handleMouseUpPassword}
                                edge="end"
                                aria-label={t(
                                  "addUser.toggleConfirmPasswordVisibilityAria"
                                )}
                              >
                                {showPassword ? (
                                  <Visibility />
                                ) : (
                                  <VisibilityOff />
                                )}
                              </IconButton>
                            </InputAdornment>
                          }
                          sx={{ borderRadius: 2 }}
                        />
                        {confirmPasswordFocused &&
                          userData.confirmPassword !== userData.password && (
                            <Typography
                              color="error"
                              variant="caption"
                              sx={{ mt: 1 }}
                            >
                              {t("addUser.confirmPasswordError")}
                            </Typography>
                          )}
                      </FormControl>
                    </Grid>
                  </Grid>
                </Paper>

                {/* Organization Information Section */}
                <Paper
                  elevation={2}
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 48,
                        height: 48,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.info.main, 0.1),
                        mr: 2,
                      }}
                    >
                      <BusinessIcon sx={{ color: theme.palette.info.main }} />
                    </Box>
                    <Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: theme.palette.text.primary,
                        }}
                      >
                        {t("addUser.orgInfoTitle")}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t("addUser.orgInfoDescription")}
                      </Typography>
                    </Box>
                  </Box>

                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{ mb: 2, fontWeight: 600 }}
                      >
                        {t("addUser.userTypeLabel")}
                      </Typography>
                      <Select
                        placeholder={t("addUser.userTypePlaceholder")}
                        options={userType}
                        value={userData.user_type}
                        onChange={handleSingleSelect("user_type")}
                        styles={{
                          ...singleSelectStyles,
                          menuPortal: (base) => ({ ...base, zIndex: 1301 }),
                        }}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        onBlur={() => setUser_typeFocused(true)}
                        aria-label={t("addUser.userTypeAria")}
                      />
                      {user_typeFocused && !userData.user_type && (
                        <Typography
                          color="error"
                          variant="caption"
                          sx={{ mt: 1, display: "block" }}
                        >
                          {t("addUser.userTypeError")}
                        </Typography>
                      )}
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{ mb: 2, fontWeight: 600 }}
                      >
                        {t("addUser.departmentLabel")}
                      </Typography>
                      <Select
                        placeholder={t("addUser.departmentPlaceholder")}
                        options={Departments}
                        value={userData.department_id}
                        onChange={handleSingleSelect("department_id")}
                        styles={{
                          ...singleSelectStyles,
                          menuPortal: (base) => ({ ...base, zIndex: 1301 }),
                        }}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        aria-label={t("addUser.departmentAria")}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{ mb: 2, fontWeight: 600 }}
                      >
                        {t("addUser.managerLabel")}
                      </Typography>
                      <Select
                        placeholder={t("addUser.managerPlaceholder")}
                        options={UsersList}
                        value={userData.manager_id}
                        onChange={handleSingleSelect("manager_id")}
                        styles={{
                          ...singleSelectStyles,
                          menuPortal: (base) => ({ ...base, zIndex: 1301 }),
                        }}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        aria-label={t("addUser.managerAria")}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{ mb: 2, fontWeight: 600 }}
                      >
                        {t("addUser.userGroupsLabel")} *
                      </Typography>
                      <Select
                        placeholder={t("addUser.userGroupsPlaceholder")}
                        options={userGroupsList}
                        isMulti
                        closeMenuOnSelect={false}
                        components={animatedComponents}
                        value={userData.user_groups}
                        onChange={handleMultiSelect("user_groups")}
                        styles={{
                          ...multiSelectStyles,
                          menuPortal: (base) => ({ ...base, zIndex: 1301 }),
                        }}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        onBlur={() => setUserGroupsFocused(true)}
                        aria-label={t("addUser.userGroupsAria")}
                      />
                      {userGroupsFocused &&
                        userData.user_groups.length === 0 && (
                          <Typography
                            color="error"
                            variant="caption"
                            sx={{ mt: 1, display: "block" }}
                          >
                            {t("addUser.userGroupsError")}
                          </Typography>
                        )}
                    </Grid>
                    <Grid size={12}>
                      <Typography
                        variant="subtitle1"
                        sx={{ mb: 2, fontWeight: 600 }}
                      >
                        {t("addUser.sitesLabel")}
                      </Typography>
                      <Select
                        placeholder={t("addUser.sitesPlaceholder")}
                        options={SitesList}
                        isMulti
                        closeMenuOnSelect={false}
                        components={animatedComponents}
                        value={userData.site_ids}
                        onChange={handleMultiSelect("site_ids")}
                        styles={{
                          ...multiSelectStyles,
                          menuPortal: (base) => ({ ...base, zIndex: 1301 }),
                        }}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        aria-label={t("addUser.sitesAria")}
                      />
                    </Grid>
                  </Grid>
                </Paper>

                {/* Permissions Section */}
                <Paper
                  elevation={2}
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 48,
                        height: 48,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.success.main, 0.1),
                        mr: 2,
                      }}
                    >
                      <SecurityIcon
                        sx={{ color: theme.palette.success.main }}
                      />
                    </Box>
                    <Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: theme.palette.text.primary,
                        }}
                      >
                        {t("addUser.permissionsTitle")}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t("addUser.permissionsDescription")}
                      </Typography>
                    </Box>
                  </Box>

                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{ mb: 2, fontWeight: 600 }}
                      >
                        {t("addUser.workPermitVisibilityLabel")} *
                      </Typography>
                      <Select
                        placeholder={t(
                          "addUser.workPermitVisibilityPlaceholder"
                        )}
                        options={visibilityTypes}
                        value={userData.work_permit_visibility}
                        onChange={handleSingleSelect("work_permit_visibility")}
                        onBlur={() => setVisibilitySettingsFocused(true)}
                        styles={{
                          ...singleSelectStyles,
                          menuPortal: (base) => ({ ...base, zIndex: 1301 }),
                        }}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        aria-label={t("addUser.workPermitVisibilityAria")}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{ mb: 2, fontWeight: 600 }}
                      >
                        {t("addUser.observationVisibilityLabel")} *
                      </Typography>
                      <Select
                        placeholder={t(
                          "addUser.observationVisibilityPlaceholder"
                        )}
                        options={visibilityTypes}
                        value={userData.observation_visibility}
                        onChange={handleSingleSelect("observation_visibility")}
                        onBlur={() => setVisibilitySettingsFocused(true)}
                        styles={{
                          ...singleSelectStyles,
                          menuPortal: (base) => ({ ...base, zIndex: 1301 }),
                        }}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        aria-label={t("addUser.observationVisibilityAria")}
                      />
                    </Grid>
                  </Grid>
                  {visibilitySettingsFocused &&
                    (!userData.work_permit_visibility ||
                      !userData.observation_visibility) && (
                      <Typography
                        color="error"
                        variant="caption"
                        sx={{ mt: 2, display: "block" }}
                      >
                        {t("addUser.visibilityError")}
                      </Typography>
                    )}
                </Paper>
              </Stack>
            </form>
          </Box>
        </DialogContent>

        <Divider />
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button
            onClick={onClose}
            variant="outlined"
            size="large"
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1.5,
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            {t("addUser.cancelButton")}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!formValid || isSubmitting}
            variant="contained"
            color="primary"
            size="large"
            startIcon={
              isSubmitting ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <PersonAddIcon />
              )
            }
            aria-label={t("addUser.addUserButtonAria")}
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1.5,
              textTransform: "none",
              fontWeight: 600,
              boxShadow: theme.shadows[2],
              "&:hover": {
                boxShadow: theme.shadows[4],
                transform: "translateY(-1px)",
              },
              "&:disabled": {
                bgcolor: theme.palette.action.disabledBackground,
                color: theme.palette.action.disabled,
              },
              transition: "all 0.2s ease",
            }}
          >
            {isSubmitting
              ? t("addUser.addingText")
              : t("addUser.addUserButton")}
          </Button>
        </DialogActions>
      </Dialog>
      <Toast
        open={toast.open}
        onClose={handleToastClose}
        severity={toast.severity}
        message={toast.message}
      />
    </>
  );
};

AddUser.propTypes = {
  onClose: PropTypes.func.isRequired,
  onUserAdd: PropTypes.func.isRequired,
};

export default AddUser;
